import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import CircularProgress from '@mui/material/CircularProgress'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { Autocomplete, Dialog, IconButton, TextField, Tooltip } from '@mui/material'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { Link } from 'react-router-dom'
import { useCategoryContext } from 'context/CategoryContext'
import {
  useGetLeafCategories,
  useGetRawProductsByCategory,
  useMoveRawProductToLeafCategory,
} from 'hooks/useRawProductCategoryReview'
import { LeafCategoryOption, RawProductByCategoryItem } from 'services/rawProductService'
import { snackbarService } from 'services/snackbarService'
import { colors } from 'styles/colors'
import CustomButton from '../../buttons/CustomButton/CustomButton'
import CustomText from '../../common/CustomText/CustomText'
import TitleText from '../../common/CustomText/TitleText'

const PAGE_SIZE = 30
const LOAD_MORE_THRESHOLD = 0.92

const RawProductCardSkeleton = () => {
  return (
    <Card sx={{ width: 200, borderRadius: 3, boxShadow: 3 }}>
      <Skeleton variant="rectangular" width={200} height={200} />
      <Stack p={1} gap={0.8}>
        <Skeleton variant="text" width="80%" height={24} />
        <Skeleton variant="text" width="55%" height={22} />
      </Stack>
    </Card>
  )
}

// A raw product has no name and no avatar of its own: the label text comes from
// what the category graph read off it, and the photo from the suggestion that
// shares its barcode.
const RawProductCard = React.memo(({
  rawProduct,
  onMove,
}: {
  rawProduct: RawProductByCategoryItem
  onMove: (rawProduct: RawProductByCategoryItem) => void
}) => {
  const image = rawProduct.images?.[0]?.medium ?? rawProduct.images?.[0]?.large ?? undefined

  // The card carries a tag, a barcode and one photo, which is enough to spot a
  // product in the wrong leaf. The rest of the row is on its own page, which the
  // card links to.
  return (
    <Card
      component={Link}
      to={`/raw-products/${rawProduct.id}`}
      // Opened beside the leaf rather than over it: the carousel is scanned for
      // products that look out of place, and following one in the same tab loses
      // the row that made it worth opening.
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'block',
        width: 200,
        borderRadius: 3,
        boxShadow: 3,
        position: 'relative',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {/* A product in the wrong leaf is obvious next to its siblings, so the fix
          belongs here rather than in a queue somewhere else. */}
      <Tooltip title="Move to another category">
        <IconButton
          size="small"
          onClick={(event) => {
            // The whole card is a link now, so moving a product has to say it is
            // not a click on that link.
            event.preventDefault()
            event.stopPropagation()
            onMove(rawProduct)
          }}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 1,
            backgroundColor: 'rgba(255,255,255,0.9)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
          }}
        >
          <DriveFileMoveOutlinedIcon sx={{ fontSize: 18, color: colors.primary }} />
        </IconButton>
      </Tooltip>
      {/* Already a product. Said on the card so a leaf can be scanned for what
          is still waiting without opening each one. */}
      {rawProduct.productId && (
        <Tooltip title="Promoted to a product">
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              zIndex: 1,
              display: 'flex',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.9)',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 22, color: colors.success }} />
          </Box>
        </Tooltip>
      )}
      {image ? (
        <CardMedia
          component="img"
          image={image}
          alt={rawProduct.tagEn ?? rawProduct.barcode}
          sx={{ width: 200, height: 200, objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            width: 200,
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.topBackground,
          }}
        >
          <CustomText text="No image" fontSize={13} color={colors.gray} />
        </Box>
      )}
      <Stack p={1} gap={0.8}>
        <TitleText
          text={rawProduct.tagEn ?? '—'}
          title={rawProduct.tagAr ?? ''}
          fontWeight={600}
          textAlign="left"
          fontSize={14}
          color={colors.black}
          lineHeight={1}
          noWrap
        />
        <CustomText
          text={rawProduct.barcode}
          fontSize={13}
          color={colors.gray}
          textAlign="left"
        />
      </Stack>
    </Card>
  )
})

const RawProductCarousel = () => {
  const { category } = useCategoryContext()

  const [page, setPage] = useState(1)
  const [rawProducts, setRawProducts] = useState<RawProductByCategoryItem[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  // The product whose category is being changed, and the leaf picked for it.
  const [moving, setMoving] = useState<RawProductByCategoryItem | null>(null)
  const [target, setTarget] = useState<LeafCategoryOption | null>(null)

  const { data: leafCategories = [] } = useGetLeafCategories()

  const { mutate: moveRawProduct, isLoading: isMoving } = useMoveRawProductToLeafCategory(
    () => {
      // It no longer belongs in this row, so it leaves without a refetch.
      setRawProducts((previous) => previous.filter((item) => item.id !== moving?.id))
      setMoving(null)
      setTarget(null)
      snackbarService.showSnackbar('Moved', 'success')
    },
    () => snackbarService.showSnackbar('Could not move it', 'error'),
  )

  const loadingRef = useRef(false)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    loop: false,
    watchResize: false,
    watchSlides: true,
  })

  const { refetch } = useGetRawProductsByCategory(
    {
      page,
      limit: PAGE_SIZE,
      categoryId: category.id,
    },
    {
      select: (res: any) => res.items,
      onSuccess: (items: RawProductByCategoryItem[]) => {
        setRawProducts((prev) => {
          if (page === 1) return items || []

          const existing = new Set(prev.map((item) => item.id))
          const next = (items || []).filter((item) => !existing.has(item.id))
          return [...prev, ...next]
        })

        setHasMore(!!items && items.length === PAGE_SIZE)
        loadingRef.current = false
        setLoadingMore(false)
      },
      onError: () => {
        loadingRef.current = false
        setLoadingMore(false)
      },
    },
  )

  useEffect(() => {
    setPage(1)
    setRawProducts([])
    setHasMore(true)
    setLoadingMore(true)
    loadingRef.current = true
    refetch()
  }, [category.id, refetch])

  useEffect(() => {
    if (page === 1) return
    refetch()
  }, [page, refetch])

  const loadNextPage = useCallback(() => {
    if (loadingRef.current || loadingMore || !hasMore) return

    loadingRef.current = true
    setLoadingMore(true)
    setPage((prev) => prev + 1)
  }, [loadingMore, hasMore])

  const onScroll = useCallback(
    (api: EmblaCarouselType) => {
      if (loadingRef.current || loadingMore || !hasMore) return

      if (api.scrollProgress() >= LOAD_MORE_THRESHOLD) {
        loadNextPage()
      }
    },
    [loadingMore, hasMore, loadNextPage],
  )

  useEffect(() => {
    if (!emblaApi) return

    const handler = () => onScroll(emblaApi)

    emblaApi.on('scroll', handler)
    return () => {
      emblaApi.off('scroll', handler)
    }
  }, [emblaApi, onScroll])

  const isInitialLoading = loadingMore && rawProducts.length === 0

  return (
    <Box>
      <Box className="embla" sx={{ width: '100%' }}>
        <Box className="embla__viewport" ref={emblaRef} sx={{ overflow: 'hidden', px: 2 }}>
          <Box className="embla__container" sx={{ display: 'flex', gap: 2 }}>
            {isInitialLoading
              ? Array.from({ length: 10 }).map((_value, index) => (
                <Box
                  key={`initial-skeleton-${index}`}
                  className="embla__slide"
                  sx={{ flex: '0 0 auto', minWidth: 0, marginTop: 2, marginBottom: 2 }}
                >
                  <RawProductCardSkeleton />
                </Box>
              ))
              : rawProducts.map((rawProduct, index) => (
                <Box
                  className="embla__slide"
                  key={rawProduct.id}
                  sx={{
                    flex: '0 0 auto',
                    minWidth: 0,
                    marginRight: index === rawProducts.length - 1 ? 2 : 0,
                    marginTop: 2,
                    marginBottom: 2,
                  }}
                >
                  <RawProductCard
                    rawProduct={rawProduct}
                    onMove={(item) => {
                      setMoving(item)
                      setTarget(null)
                    }}
                  />
                </Box>
              ))}

            {!isInitialLoading && rawProducts.length === 0 && (
              <Box py={4} px={1}>
                <CustomText text="No raw products here" fontSize={14} color={colors.gray} />
              </Box>
            )}

            {!isInitialLoading && hasMore && (
              <Box
                className="embla__slide"
                sx={{
                  flex: '0 0 48px',
                  minWidth: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 2,
                  marginBottom: 2,
                  marginRight: 2,
                }}
              >
                {loadingMore ? <CircularProgress size={22} /> : null}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={Boolean(moving)}
        onClose={() => setMoving(null)}
        fullWidth
        maxWidth="sm"
      >
        <Stack p={3} gap={2}>
          <CustomText
            text={moving?.tagEn ?? moving?.barcode ?? ''}
            fontSize={16}
            fontWeight={600}
            color={colors.black}
          />
          <CustomText text={moving?.barcode ?? ''} fontSize={13} color={colors.gray} />

          <Autocomplete
            options={leafCategories}
            value={target}
            onChange={(_event, value) => setTarget(value)}
            getOptionLabel={(option) => option.pathEN}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} size="small" label="Move to" />}
          />

          <Stack direction="row" gap={1} justifyContent="flex-end">
            <CustomButton
              text="Cancel"
              variant="outlined"
              height={40}
              onClick={() => setMoving(null)}
            />
            <CustomButton
              text="Move"
              height={40}
              isLoading={isMoving}
              disabled={!target || !moving}
              onClick={() => {
                if (moving && target) {
                  moveRawProduct({ rawProductId: moving.id, leafCategoryId: target.id })
                }
              }}
            />
          </Stack>
        </Stack>
      </Dialog>
    </Box>
  )
}

export default RawProductCarousel
